'use strict';
const _ = require('lodash');
const repeaterUtils = require('./repeaterUtils/repeaterUtils');
const modelUtils = require('./modelUtils');
const HooksManager = require('./RMIHooks/hooksManager');

const DATA = 'data',
  FULL_DATA = 'fullData',
  DESIGN = 'design',
  STATE = 'state',
  TYPE = 'type',
  PROPS = 'props',
  PARENT = 'parent',
  EVENTS = 'events',
  LAYOUT = 'layout',
  BEHAVIOR = 'behavior',
  PUBLIC_API = 'publicAPI',
  IS_DISPLAYED = 'isDisplayed',
  STYLE = 'style',
  ID = 'id',
  CHILDREN = 'children',
  DISPLAYED_ONLY_COMPONENTS = 'displayedOnlyComponents',
  DISPLAYED_ROOT = 'displayedRoot';

const GLOBAL_SCOPE = 'GLOBAL_SCOPE';
const COMPONENT_SCOPE = 'COMPONENT_SCOPE';

function RemoteModelInterface(modelJson, onUpdateCallback, options) {
  this._model = modelJson || {
    components: {},
    connections: {},
    componentsScopes: {},
    navigation: {},
    siteStructure: {},
    eventHandlers: {},
    EventTypes: {},
    sessionInfo: {},
    appsData: {},
    siteMemberData: {uid: null, permissions: null, onUserLogin: []},
    scopesHierarchy: [{type: GLOBAL_SCOPE, id: GLOBAL_SCOPE, additionalData: {}}]
  };
  this._onUpdateCallback = onUpdateCallback;

  const optionsHookManager = _.get(options, '_hooksManager');
  const optionsTransaction = _.get(options, '_transaction');
  this._hooksManager = optionsHookManager ? optionsHookManager : new HooksManager();
  this._transaction =  optionsTransaction ? optionsTransaction : {_onTransaction: false, _transactionData: {}}
}

function getCurrentScopeId(onlyNested) {
  return onlyNested ? _.get(this._model.scopesHierarchy, '0.id') : _.map(this._model.scopesHierarchy, scopeHierarchy => _.get(scopeHierarchy, 'id'));
}

function generateDefaultCompStructure() {
  return {
    [PARENT]: null,
    [STATE]: {},
    [TYPE]: null,
    [DATA]: {},
    [FULL_DATA]: {},
    [DESIGN]: {},
    [PROPS]: {},
    [LAYOUT]: {},
    [EVENTS]: [],
    [IS_DISPLAYED]: false,
    [ID]: null,
    [PUBLIC_API]: {},
    [STYLE]: {},
    [CHILDREN]: [], 
    [DISPLAYED_ONLY_COMPONENTS]: [],
    [DISPLAYED_ROOT]: null
  };
};

RemoteModelInterface.prototype.addComponent = function (compId, compDescriptor) {
  const comp = generateDefaultCompStructure();
  for (const key in comp) {
    comp[key] = compDescriptor[key] || comp[key];
  }
  comp[PARENT] = compDescriptor[PARENT];
  comp[TYPE] = compDescriptor[TYPE];
  comp[ID] = compDescriptor[ID];
  comp[DISPLAYED_ROOT] = compDescriptor[DISPLAYED_ROOT];
  this._model.components[compId] = comp;
  this._model.componentsScopes[compId] = GLOBAL_SCOPE;
};

RemoteModelInterface.prototype.addNavigation = function (navigationData) {
  this._model.navigation = navigationData;
};

RemoteModelInterface.prototype.addSiteStructure = function (siteStructureData) {
  this._model.siteStructure = siteStructureData;
};

RemoteModelInterface.prototype.addConnections = function (connectionsModel) {
  this._model.connections = connectionsModel;
};

RemoteModelInterface.prototype.resetComponentsScope = function () {
  for (const compId in this._model.components) {
    const displayedOnlyCompIds = this._model.components[compId].displayedOnlyComponents;

    displayedOnlyCompIds.forEach(displayedOnlyCompId => this._model.componentsScopes[displayedOnlyCompId] = null);
  }
};

RemoteModelInterface.prototype.addEventTypes = function (EventTypes) {
  this._model.EventTypes = EventTypes;
};

RemoteModelInterface.prototype.addSiteMemberData = function (siteMemberData) {
  const shouldTriggerOnLoginCallback = siteMemberData.uid && siteMemberData.uid !== this._model.siteMemberData.uid;
  _.assign(this._model.siteMemberData, siteMemberData);
  if (shouldTriggerOnLoginCallback) {
    const onUserLoginCallbacks = this._model.siteMemberData.onUserLogin;
    _.forEach(onUserLoginCallbacks, callback => {
      callback(siteMemberData);
    });

  }
};

RemoteModelInterface.prototype.addAppsData = function (appsData) {
  this._model.appsData = appsData
};

RemoteModelInterface.prototype.getAppsData = function () {
  return _.cloneDeep(this._model.appsData);
};

RemoteModelInterface.prototype.getAppData = function (appDefinitionId) {
  return _.cloneDeep(_.get(this._model.appsData, appDefinitionId));
};

RemoteModelInterface.prototype.addOnUserLoginCallback = function (callback) {
  this._model.siteMemberData.onUserLogin.push(callback);
};

RemoteModelInterface.prototype.addSessionInfoProp = function (addSessionInfoProp) {
  _.assign(this._model.sessionInfo, addSessionInfoProp);
};

//Getters

RemoteModelInterface.prototype.getComp = function (compId) {
  return _.cloneDeep(_.get(this._model.components, compId));
};

RemoteModelInterface.prototype.getState = function (compId) {
  return _.cloneDeep(_.get(this._model.components, [compId, STATE]));
};

RemoteModelInterface.prototype.getData = function (compId) {
  return _.cloneDeep(_.get(this._model.components, [compId, DATA]));
};

RemoteModelInterface.prototype.getStyle = function (compId) {
  return _.cloneDeep(_.get(this._model.components, [compId, STYLE]));
};

RemoteModelInterface.prototype.getDesign = function (compId) {
  return _.cloneDeep(_.get(this._model.components, [compId, DESIGN]));
};

RemoteModelInterface.prototype.getType = function (compId) {
  return _.cloneDeep(_.get(this._model.components, [compId, TYPE]));
};

RemoteModelInterface.prototype.getProps = function (compId) {
  return _.cloneDeep(_.get(this._model.components, [compId, PROPS]));
};

RemoteModelInterface.prototype.getEvents = function (compId) {
  return _.cloneDeep(_.get(this._model.components, [compId, EVENTS]));
};

RemoteModelInterface.prototype.getLayout = function (compId) {
  return _.cloneDeep(_.get(this._model.components, [compId, LAYOUT]));
};

RemoteModelInterface.prototype.getId = function (compId) {
  return _.cloneDeep(_.get(this._model.components, [compId, ID]));
};

RemoteModelInterface.prototype.getEventTypes = function () {
  return _.cloneDeep(this._model.EventTypes);
};

RemoteModelInterface.prototype.getPublicAPI = function (compId) {
  const publicApi = _.get(this._model.components, [compId, PUBLIC_API]);
  if (_.isFunction(publicApi)) {
    return publicApi;
  }
  return _.cloneDeep(publicApi);
};

RemoteModelInterface.prototype.getCallbackById = function (callbackId, newFormat) {
  const eventHandler = this._model.eventHandlers[callbackId]; // TODO - backward compatibility - remove after SDK next release
  if(newFormat) {
    return eventHandler;
  }
  return _.get(eventHandler, 'callback');
};

RemoteModelInterface.prototype.getParent = function (compId) {
  return _.cloneDeep(_.get(this._model.components, [compId, PARENT]));
};

RemoteModelInterface.prototype.getChildren = function (parentId) {
  return _.clone(_.get(this._model.components, [parentId, CHILDREN]));
};

RemoteModelInterface.prototype.getSiteStructure = function () {
  return _.cloneDeep(this._model.siteStructure);
};

RemoteModelInterface.prototype.getNavigationInfo = function () {
  return _.cloneDeep(this._model.navigation);
};

RemoteModelInterface.prototype.getFullDataWithOverrides = function (compId) {
  return _.defaults(this.getData(compId), getFullData.call(this, compId));
};

RemoteModelInterface.prototype.getSiteMemberData = function (controllerDataId) {
  const siteMemberData = _.cloneDeep(this._model.siteMemberData);
  if (controllerDataId) {
    const controllerId = _.findKey(this._model.components, ['data.id', controllerDataId]);
    const controllerData = this.getData(controllerId);
    const appDefinitionId = _.get(controllerData, 'applicationId');
    const appData = _.get(this._model.appsData, appDefinitionId);
    if (appData) {
      _.assign(siteMemberData, _.pick(appData, ['instance', 'instanceId']))
    }
  }
  return siteMemberData;
};

RemoteModelInterface.prototype.getSessionInfo = function () {
  return _.cloneDeep(this._model.sessionInfo);
};


//Setters

RemoteModelInterface.prototype.setDataWithoutUpdate = function (compId, partialData) {
  set.call(this, compId, DATA, partialData);
};

RemoteModelInterface.prototype.setData = function (compId, partialData) {
  set.call(this, compId, DATA, partialData, this._onUpdateCallback);
};

RemoteModelInterface.prototype.setStyle = function (compId, partialData, cb) {
  set.call(this, compId, STYLE, partialData, function (cid, property, partial) {
    this._onUpdateCallback.call(this, cid, property, partial, cb);
  }.bind(this));
};

RemoteModelInterface.prototype.setDesign = function (compId, partialData) {
  set.call(this, compId, DESIGN, partialData, this._onUpdateCallback);
};

RemoteModelInterface.prototype.setProps = function (compId, partialProps, cb) {
  set.call(this, compId, PROPS, partialProps, function (cid, property, partial) {
    this._onUpdateCallback.call(this, cid, property, partial, cb);
  }.bind(this));
};

RemoteModelInterface.prototype.setLayout = function (compId, partialLayout) {
  set.call(this, compId, LAYOUT, partialLayout, this._onUpdateCallback);
};

RemoteModelInterface.prototype.setPublicAPI = function (compId, api) {
  const comp = _.get(this._model.components, compId);
  if (comp) {
    comp[PUBLIC_API] = api;
  }
};

RemoteModelInterface.prototype.getCurrentScope = function () {
  return _.clone(_.get(this._model.scopesHierarchy, 0));
};

function getFullData(compId) {
  return _.cloneDeep(_.get(this._model.components, [compId, FULL_DATA]));
}

function executeBehavior(behaviorType, behaviorName, params, compId, callback) {
  const behavior = {
    type: behaviorType,
    name: behaviorName,
    targetId: compId,
    params
  };

  if (this._onUpdateCallback) {
    this._onUpdateCallback(compId, BEHAVIOR, behavior, callback);
  }
}

RemoteModelInterface.prototype.executeCompBehavior = function (compId, behaviorName, params, callback) {
  executeBehavior.call(this, 'comp', behaviorName, params, compId, callback);
};

RemoteModelInterface.prototype.executeAnimation = function (compId, animationName, params, callback) {
  executeBehavior.call(this, 'animation', animationName, params, compId, callback);
};

RemoteModelInterface.prototype.setUpdateCallback = function (onUpdateCallback) {
  this._onUpdateCallback = onUpdateCallback;
};

RemoteModelInterface.prototype.registerEvent = function (contextId, compId, eventType, controllerId, callback) {
  let callbackId;
  if(callback === undefined) { //TODO - backward compatibility - remove after SDK next release
    callback = controllerId;
    controllerId = undefined;
  }
  if (_.isFunction(callback)) {
    callbackId = guid();
    this._model.eventHandlers[callbackId] = {callback, contextId, controllerId};
  } else {
    callbackId = callback;
  }
  const eventDescriptor = {
    contextId,
    eventType,
    callbackId
  };
  const component = _.get(this._model, ['components', compId]);
  if (component !== undefined) {
    this._hooksManager.executeHooksAndUpdateValue(this, 'registerEvent', component.type, compId, [contextId, eventType, controllerId, callback]);
    component.events.push({eventType, callbackId});
    if (this._transaction._onTransaction) {
      addItemToTransactionQueue.call(this, compId, 'registerEvent', eventDescriptor);
      return;
    }
    if (_.isFunction(this._onUpdateCallback)) {
      this._onUpdateCallback(compId, 'registerEvent', eventDescriptor);
    }
  }
};

RemoteModelInterface.prototype.toJson = function () {
  return this._model;
};

//General

RemoteModelInterface.prototype.getComponents = function () {
  const templateComponents = {};
  for (const compId in this._model.components) { //eslint-disable-line guard-for-in
    if (!modelUtils.isTemplateComp(this._model, compId)) {
      templateComponents[compId] = this.getComp(compId);
    }
  }

  return templateComponents;
};

RemoteModelInterface.prototype.getScopedRMI = function (controllerId) {
  const scopedRMI = new this.constructor(this._model, this._onUpdateCallback, {_hooksManager : this._hooksManager, _transaction: this._transaction});
  scopedRMI.getCompIdsFromRole = scopedRMI.getCompIdsFromRole.bind(scopedRMI, controllerId);
  scopedRMI.getCompIdsFromType = scopedRMI.getCompIdsFromType.bind(scopedRMI, controllerId);
  scopedRMI.getConfig = scopedRMI.getConfig.bind(scopedRMI, controllerId);

  return scopedRMI;
};

RemoteModelInterface.prototype.getNestedScopedRMI = function (model) {
  return new this.constructor(model || this._model, this._onUpdateCallback, {_hooksManager : this._hooksManager, _transaction: this._transaction});
};

RemoteModelInterface.prototype.getCompIdsFromType = function (controllerId, type, onlyNested) {
  const components = _.get(this._model.connections, controllerId);
  const compIds = _.flatMap(components, Object.keys);
  const comps = [];
  const acceptedScopes = getCurrentScopeId.call(this, onlyNested);

  compIds.forEach(function (compId) {
    const compType = this.getType(compId);
    if (compType === type && acceptedScopes.includes(_.get(this._model.componentsScopes, compId))) {
      comps.push(compId);
    }
  }, this);
  return comps;
};

RemoteModelInterface.prototype.getCompIdsFromRole = function (controllerId, role, onlyNested) {
  const connectionConfigs = _.get(this._model.connections, [controllerId, role]);
  const acceptedScopes = getCurrentScopeId.call(this, onlyNested);
  const compIds = connectionConfigs ? Object.keys(connectionConfigs) : [];
  return compIds.filter(compId => acceptedScopes.includes(_.get(this._model.componentsScopes, compId)));
};

RemoteModelInterface.prototype.getConfig = function (controllerId, compId, role, onlyNested) {
  const connectionConfigs = _.get(this._model.connections, [controllerId, role]);
  const acceptedScopes = getCurrentScopeId.call(this, onlyNested);

  return connectionConfigs && connectionConfigs.hasOwnProperty(compId) && acceptedScopes.includes(_.get(this._model.componentsScopes, compId)) ? connectionConfigs[compId] : {};
};

RemoteModelInterface.prototype.updateModel = function (modelUpdates) {
  for (const compId in modelUpdates) { //eslint-disable-line guard-for-in
    const defaultComp = generateDefaultCompStructure();
    const comp = _.get(this._model.components, compId);
    const compUpdates = modelUpdates[compId];
    if (!comp) {
      continue;
    }

    for (const keyToUpdate in compUpdates) {
      if (!_.has(comp, keyToUpdate)) {
        continue;
      }
      if (_.isObject(comp[keyToUpdate])) {
        if (compUpdates[keyToUpdate]) {
          _.assign(comp[keyToUpdate], compUpdates[keyToUpdate]);
        } else { // for modes change, when key should be initial to default value
          comp[keyToUpdate] = defaultComp[keyToUpdate];
        }
      } else {
        comp[keyToUpdate] = compUpdates[keyToUpdate];
      }
    }
  }
};

RemoteModelInterface.prototype.getScopedRMIByCompId = function (compId) {
  const itemContext = this.getRepeaterItemContext(compId);
  if (_.isEmpty(itemContext)) {
    return this;
  }

  const {displayedRoot, itemId} = itemContext;

  return this.getScopedRMIForRepeaterItem(displayedRoot, itemId);
};

RemoteModelInterface.prototype.getScopedRMIForRepeaterItem = function (repeaterId, itemId) {
  const model = _.clone(this._model);
  const templateCompIds = modelUtils.getAllCompsUnderRoot(model, repeaterId);
  const itemCompIds = templateCompIds.map(compId => repeaterUtils.structure.getUniqueDisplayedId(compId, itemId));

  model.scopesHierarchy = _.clone(model.scopesHierarchy);
  model.scopesHierarchy.unshift({type: COMPONENT_SCOPE, id: repeaterId, compId: repeaterId, additionalData: {itemId}});

  model.componentsScopes = _.clone(model.componentsScopes);
  templateCompIds.forEach(templateCompId => model.componentsScopes[templateCompId] = null);
  itemCompIds.forEach(itemCompId => model.componentsScopes[itemCompId] = repeaterId);

  return this.getNestedScopedRMI(model);
};


RemoteModelInterface.prototype.getRepeaterItemContext = function (compId) {
  const displayedRoot = _.get(this._model.components, [compId, DISPLAYED_ROOT]);

  if (!displayedRoot) {
    return {};
  }

  const itemId = repeaterUtils.structure.getItemId(compId);
  return {displayedRoot, itemId}
};

RemoteModelInterface.prototype.startTransaction = function () {
  this._transaction._onTransaction = true;
};

RemoteModelInterface.prototype.endTransaction = function () {
  if (this._onUpdateCallback) {
    this._onUpdateCallback(null, 'executeBatch', this._transaction._transactionData);
  }
  this._transaction._onTransaction = false;
  this._transaction._transactionData = {};

};

RemoteModelInterface.prototype.setBatchData = function (newData) {
  _.forEach(newData, function (dataByProperty, compId)  {
    _.forEach(dataByProperty, function (partialData, propertyType) {
      switch (propertyType) {
      case 'registerEvent':
        _.forEach(partialData, function (eventDescriptor) {
          this.registerEvent.call(this, eventDescriptor.contextId, compId, eventDescriptor.eventType, eventDescriptor.controllerId, eventDescriptor.callbackId)
        }.bind(this));
        break;
      default:
        const funcName = `set${_.startCase(propertyType)}`;
        this[funcName].call(this, compId, partialData);
        break;
      }

    }.bind(this));
  }.bind(this));
};

RemoteModelInterface.prototype.registerHook = function (hookName, compType, callback) {
  this._hooksManager.registerHook(hookName, compType , callback);
};

RemoteModelInterface.prototype.getHooks = function () {
  return this._hooksManager.HOOKS
};

//Utility

function guid() {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }

  return s4() + s4() + '-' + s4() + '-' + s4() + '-' +
    s4() + '-' + s4() + s4() + s4();
}

function set(compId, property, partial, onUpdateCallback) {
  const comp = _.get(this._model, ['components', compId]);
  if (!comp) {
    return;
  }

  this._hooksManager.executeHooksAndUpdateValue(this, property, comp.type, compId, [partial, onUpdateCallback]);
  if (comp[IS_DISPLAYED]) {
    this._model.components[compId][property] = _.defaults({}, partial, comp[property]);
  }
  if (onUpdateCallback) {
    if (this._transaction._onTransaction) {
      addItemToTransactionQueue.call(this, compId, property, partial);
    } else {
      onUpdateCallback(compId, property, partial);
    }
  }
}

function addItemToTransactionQueue(compId, property, partial) {
  const transactionData = this._transaction._transactionData;
  if (!transactionData[compId]) {
    transactionData[compId]= {};
  }

  if (property === 'registerEvent') {
    if (!transactionData[compId][property]) {
      transactionData[compId][property] = [];
    }
    transactionData[compId][property].push(partial);
    return;
  }
  if (!transactionData[compId][property]) {
    transactionData[compId][property] = {};
  }
  _.assign(transactionData[compId][property], partial);
}


module.exports = RemoteModelInterface;



// WEBPACK FOOTER //
// ./src/RemoteModelInterface.js